<script setup>
// Config do nó Mover no Kanban — funil → etapa (cascata) + dados do lead.
// Espelha MoveToStageConfig do original (modo manual/IA, nome/status/motivo do lead).
import { computed, inject, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import FlowField from './FlowField.vue';

const props = defineProps({ node: { type: Object, required: true } });

const { t } = useI18n();

const aux = inject('flowAux', {});
const d = computed(() => props.node.data);

const funnelOptions = computed(() => [
  { value: '', label: t('CHATBOT.nodeConfig.moveToStage.funnelPlaceholder') },
  ...(aux.funnels || []).map(f => ({ value: f.id, label: f.name })),
]);
const stages = computed(
  () =>
    (aux.funnels || []).find(f => f.id === Number(d.value.funnelId))?.stages ||
    []
);
const stageOptions = computed(() => [
  { value: '', label: t('CHATBOT.nodeConfig.moveToStage.stagePlaceholder') },
  ...stages.value.map(s => ({ value: s.id, label: s.name })),
]);

const onFunnelChange = v => {
  d.value.funnelId = v;
  d.value.stageId = '';
  d.value.fieldValues = {};
};

const LEAD_STATUS = computed(() => [
  { value: '', label: t('CHATBOT.nodeConfig.moveToStage.leadStatusNoChange') },
  { value: 'open', label: t('CHATBOT.nodeConfig.moveToStage.leadStatusOpen') },
  { value: 'won', label: t('CHATBOT.nodeConfig.moveToStage.leadStatusWon') },
  { value: 'lost', label: t('CHATBOT.nodeConfig.moveToStage.leadStatusLost') },
]);
const showCloseReason = computed(() =>
  ['won', 'lost'].includes(d.value.leadStatus)
);

onMounted(() => {
  if (!d.value.mode) d.value.mode = 'manual';
});
</script>

<template>
  <div>
    <FlowField
      :model-value="d.mode"
      :label="t('CHATBOT.nodeConfig.moveToStage.modeLabel')"
      type="buttongroup"
      :options="[
        {
          value: 'manual',
          label: t('CHATBOT.nodeConfig.moveToStage.modeManual'),
        },
        { value: 'ai', label: t('CHATBOT.nodeConfig.moveToStage.modeAI') },
      ]"
      @update:model-value="d.mode = $event"
    />
    <FlowField
      :model-value="d.funnelId"
      :label="t('CHATBOT.nodeConfig.moveToStage.funnelLabel')"
      type="select"
      :options="funnelOptions"
      @update:model-value="onFunnelChange"
    />

    <template v-if="d.funnelId">
      <FlowField
        v-if="d.mode !== 'ai'"
        :model-value="d.stageId"
        :label="t('CHATBOT.nodeConfig.moveToStage.stageLabel')"
        type="select"
        :options="stageOptions"
        @update:model-value="d.stageId = $event"
      />
      <FlowField
        v-else
        :model-value="d.stageVariable"
        :label="t('CHATBOT.nodeConfig.moveToStage.stageVariableLabel')"
        type="text"
        placeholder="ex: etapaEscolhida"
        @update:model-value="d.stageVariable = $event"
      />

      <div class="pt-2 mt-2 border-t border-n-weak">
        <p class="mb-2 text-[0.6875rem] font-semibold text-n-slate-11">
          {{ t('CHATBOT.nodeConfig.moveToStage.leadDataTitle') }}
        </p>
        <FlowField
          :model-value="d.customName"
          :label="t('CHATBOT.nodeConfig.moveToStage.leadNameLabel')"
          type="text"
          :placeholder="t('CHATBOT.nodeConfig.moveToStage.leadNamePlaceholder')"
          picker
          @update:model-value="d.customName = $event"
        />
        <FlowField
          :model-value="d.leadStatus"
          :label="t('CHATBOT.nodeConfig.moveToStage.leadStatusLabel')"
          type="select"
          :options="LEAD_STATUS"
          @update:model-value="d.leadStatus = $event"
        />
        <FlowField
          v-if="showCloseReason"
          :model-value="d.closeReason"
          :label="
            t('CHATBOT.nodeConfig.moveToStage.closeReasonLabel', {
              type:
                d.leadStatus === 'won'
                  ? t('CHATBOT.nodeConfig.moveToStage.closeReasonWon')
                  : t('CHATBOT.nodeConfig.moveToStage.closeReasonLost'),
            })
          "
          type="text"
          :placeholder="
            t('CHATBOT.nodeConfig.moveToStage.closeReasonPlaceholder')
          "
          @update:model-value="d.closeReason = $event"
        />
      </div>
    </template>

    <p class="mt-3 text-xs text-n-slate-10">
      {{ t('CHATBOT.nodeConfig.moveToStage.howStep1') }}
    </p>
  </div>
</template>
